/**
 * Shared coauthor state management for post profile editors.
 */
import { useCallback, useEffect, useState } from "react";
import type { CoauthorSuggestion } from "types/coauthor";

export interface ExistingPostCoauthor {
  id: number;
  type: CoauthorSuggestion["type"];
  created_at?: string;
  set_at?: string;
  status?: string;
}

export interface PostCoauthorState {
  coauthors: ExistingPostCoauthor[];
  newCoauthors: CoauthorSuggestion[];
  removedCoauthors: CoauthorSuggestion[];
  initializeCoauthors: (coauthors: ExistingPostCoauthor[]) => void;
  handleCoauthorsChange: (added: CoauthorSuggestion[], removed: CoauthorSuggestion[]) => void;
  resetCoauthorChanges: () => void;
}

/**
 * Normalizes loosely typed API coauthor payloads to the subset the editor needs.
 */
export function normalizeExistingCoauthors(value: unknown): ExistingPostCoauthor[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((coauthor) => {
    if (!coauthor || typeof coauthor !== "object") {
      return [];
    }

    const id = Reflect.get(coauthor, "id");
    const type = Reflect.get(coauthor, "type");

    if (typeof id !== "number" || (type !== "user" && type !== "team")) {
      return [];
    }

    const createdAt = Reflect.get(coauthor, "created_at");
    const setAt = Reflect.get(coauthor, "set_at");
    const status = Reflect.get(coauthor, "status");

    return [
      {
        id,
        type,
        created_at: typeof createdAt === "string" ? createdAt : undefined,
        set_at: typeof setAt === "string" ? setAt : undefined,
        status: typeof status === "string" ? status : undefined,
      },
    ];
  });
}

export function usePostCoauthorState(privacy: string): PostCoauthorState {
  const [coauthors, setCoauthors] = useState<ExistingPostCoauthor[]>([]);
  const [newCoauthors, setNewCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [removedCoauthors, setRemovedCoauthors] = useState<CoauthorSuggestion[]>([]);

  useEffect(() => {
    if (privacy === "anonymous" && (coauthors.length > 0 || newCoauthors.length > 0 || removedCoauthors.length > 0)) {
      setCoauthors([]);
      setNewCoauthors([]);
      setRemovedCoauthors([]);
    }
  }, [privacy, coauthors.length, newCoauthors.length, removedCoauthors.length]);

  const initializeCoauthors = useCallback((nextCoauthors: ExistingPostCoauthor[]) => {
    setCoauthors(nextCoauthors);
    setNewCoauthors([]);
    setRemovedCoauthors([]);
  }, []);

  const handleCoauthorsChange = useCallback(
    (added: CoauthorSuggestion[], removed: CoauthorSuggestion[]) => {
      setCoauthors((previousCoauthors) =>
        previousCoauthors.filter(
          (coauthor) =>
            !removed.some(
              (removedCoauthor) =>
                removedCoauthor.id === coauthor.id && removedCoauthor.type === coauthor.type
            )
        )
      );
      setNewCoauthors(added);
      setRemovedCoauthors(removed);
    },
    []
  );

  const resetCoauthorChanges = useCallback(() => {
    setNewCoauthors([]);
    setRemovedCoauthors([]);
  }, []);

  return {
    coauthors,
    newCoauthors,
    removedCoauthors,
    initializeCoauthors,
    handleCoauthorsChange,
    resetCoauthorChanges,
  };
}
